import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ChevronRight, PenTool, RotateCcw, Check, Loader2 } from "lucide-react";

const SignatureFlow = ({ rental, client, vehicle, onComplete, onCancel, axiosAuth }) => {
  const [step, setStep] = useState(1);
  const [privacyPages, setPrivacyPages] = useState([]);
  const [privacyLoading, setPrivacyLoading] = useState(true);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const pointsRef = useRef([]);

  // Load privacy PDF pages
  useEffect(() => {
    const loadPrivacy = async () => {
      try {
        const api = axiosAuth();
        const res = await api.get("/signatures/privacy-preview");
        setPrivacyPages(res.data.pages || []);
      } catch {
        setPrivacyPages([]);
      } finally {
        setPrivacyLoading(false);
      }
    };
    loadPrivacy();
  }, [axiosAuth]);

  // Canvas setup
  useEffect(() => {
    if (step === 4 && canvasRef.current) {
      const canvas = canvasRef.current;
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 2.5;
    }
  }, [step]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top, pressure: e.pressure || 0.5 };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const pos = getPos(e);
    pointsRef.current.push(pos);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const pos = getPos(e);
    const pressure = pos.pressure;
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = Math.max(1, 2.5 * (pressure * 2));
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    pointsRef.current.push(pos);
    setHasDrawn(true);
  };

  const endDraw = (e) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    pointsRef.current = [];
    setHasDrawn(false);
  };

  const confirmSignature = () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL("image/png");
    const b64 = dataUrl.split(",")[1];
    onComplete(b64);
  };

  const fmtDate = (d) => {
    if (!d) return "-";
    const [y, m, dd] = d.split("-");
    return `${dd}/${m}/${y}`;
  };

  const days = rental?.start_date && rental?.end_date
    ? Math.max(1, Math.ceil((new Date(rental.end_date) - new Date(rental.start_date)) / 86400000))
    : "-";

  return (
    <div className="fixed inset-0 z-[100] bg-white flex flex-col" data-testid="signature-flow">
      {/* Progress bar */}
      <div className="h-1.5 bg-slate-100 w-full">
        <div className="h-full bg-red-600 transition-all duration-300" style={{ width: `${(step / 4) * 100}%` }} />
      </div>

      {/* Header */}
      <div className="px-6 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
        <div className="flex items-center gap-3">
          <PenTool className="w-5 h-5 text-red-600" />
          <div>
            <p className="text-sm font-bold text-slate-900">Firma Contratto {rental?.contract_number}</p>
            <p className="text-xs text-slate-500">{client?.name} {client?.surname} - {vehicle?.brand} {vehicle?.model} ({vehicle?.plate})</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Passaggio {step}/4</span>
          <Button variant="ghost" size="sm" onClick={onCancel} className="text-xs text-slate-500 hover:text-red-600">Annulla</Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6 max-w-3xl mx-auto w-full">

        {/* Step 1: Privacy */}
        {step === 1 && (
          <div className="space-y-4" data-testid="signature-step-1">
            <h2 className="text-xl font-bold text-slate-900">Autorizzazione Privacy</h2>
            {privacyLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-red-600 animate-spin" />
              </div>
            ) : privacyPages.length > 0 ? (
              <div className="space-y-4">
                {privacyPages.map((page, i) => (
                  <img key={i} src={`data:image/png;base64,${page}`} alt={`Privacy pagina ${i + 1}`} className="w-full rounded-lg border border-slate-200 shadow-sm" draggable={false} />
                ))}
              </div>
            ) : (
              <div className="p-5 bg-slate-50 rounded-xl border border-slate-200 text-sm text-slate-500 text-center">
                Documento privacy non disponibile
              </div>
            )}
          </div>
        )}

        {/* Step 2: Client Data & Rental Summary */}
        {step === 2 && (
          <div className="space-y-5" data-testid="signature-step-2">
            <h2 className="text-xl font-bold text-slate-900">Dati Cliente e Riepilogo Noleggio</h2>

            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Dati Cliente</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-400">Nome:</span> <span className="font-medium text-slate-900">{client?.name} {client?.surname}</span></div>
                <div><span className="text-slate-400">Cod. Fiscale:</span> <span className="font-medium text-slate-900">{client?.fiscal_code || "-"}</span></div>
                <div><span className="text-slate-400">Indirizzo:</span> <span className="font-medium text-slate-900">{client?.address || "-"}{client?.city ? `, ${client.city}` : ""}</span></div>
                <div><span className="text-slate-400">Telefono:</span> <span className="font-medium text-slate-900">{client?.phone || "-"}</span></div>
                <div><span className="text-slate-400">Patente:</span> <span className="font-medium text-slate-900">{client?.license_number || "-"}</span></div>
                <div><span className="text-slate-400">Email:</span> <span className="font-medium text-slate-900">{client?.email || "-"}</span></div>
              </div>
            </div>

            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Riepilogo Noleggio</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-400">Veicolo:</span> <span className="font-medium text-slate-900">{vehicle?.brand} {vehicle?.model}</span></div>
                <div><span className="text-slate-400">Targa:</span> <span className="font-medium text-slate-900">{vehicle?.plate}</span></div>
                <div><span className="text-slate-400">Consegna:</span> <span className="font-medium text-slate-900">{fmtDate(rental?.start_date)} {rental?.start_time || ""}</span></div>
                <div><span className="text-slate-400">Riconsegna:</span> <span className="font-medium text-slate-900">{fmtDate(rental?.end_date)} {rental?.end_time || ""}</span></div>
                <div><span className="text-slate-400">Durata:</span> <span className="font-medium text-slate-900">{days} giorni</span></div>
                <div><span className="text-slate-400">Tariffa:</span> <span className="font-medium text-slate-900">{rental?.daily_rate} EUR/giorno</span></div>
                <div><span className="text-slate-400">Totale:</span> <span className="font-bold text-slate-900 text-base">{rental?.total_amount?.toFixed(2)} EUR</span></div>
                <div><span className="text-slate-400">Acconto:</span> <span className="font-medium text-slate-900">{(rental?.deposit_amount || 0).toFixed(2)} EUR</span></div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Contract Terms */}
        {step === 3 && (
          <div className="space-y-4" data-testid="signature-step-3">
            <h2 className="text-xl font-bold text-slate-900">Termini e Condizioni del Contratto</h2>
            <div className="p-5 bg-slate-50 rounded-xl border border-slate-200 text-sm text-slate-700 leading-relaxed space-y-3">
              <p><b>1. Condizioni generali.</b> Il presente contratto di noleggio e' regolato dalle condizioni qui riportate. Il veicolo viene consegnato in buono stato e dovra' essere restituito nelle medesime condizioni.</p>
              <p><b>2. Conclusione del contratto.</b> Il contratto si perfeziona con la consegna del veicolo e la sottoscrizione del presente modulo.</p>
              <p><b>3. Obblighi del Locatario.</b> Il Locatario si impegna a: utilizzare il veicolo con diligenza, rispettare il Codice della Strada, non cedere a terzi il veicolo, non utilizzarlo per competizioni o gare.</p>
              <p><b>4. Assicurazione.</b> Il veicolo e' coperto da assicurazione RCA. Eventuali danni non coperti dalla polizza sono a carico del Locatario.</p>
              <p><b>5. Restituzione.</b> Il veicolo deve essere restituito nella data e ora concordate. Ritardi comportano l'addebito di giorni aggiuntivi.</p>
              <p><b>6. Carburante.</b> Il veicolo viene consegnato con un determinato livello di carburante e deve essere restituito con lo stesso livello.</p>
              <p><b>7. Danni e furto.</b> In caso di incidente o furto, il Locatario deve avvisare immediatamente l'Azienda e le autorita' competenti.</p>
              <p><b>8. Penali.</b> Il mancato rispetto delle condizioni comporta l'applicazione delle penali previste e la risoluzione del contratto.</p>
              <p className="font-medium mt-4">Proseguendo con la firma, il Locatario dichiara di aver letto e accettato integralmente le condizioni sopra riportate.</p>
            </div>
          </div>
        )}

        {/* Step 4: Signature */}
        {step === 4 && (
          <div className="space-y-4" data-testid="signature-step-4">
            <h2 className="text-xl font-bold text-slate-900">Firma del Cliente</h2>
            <div className="text-center py-2">
              <p className="text-lg font-bold text-slate-800">{client?.name} {client?.surname}</p>
              <p className="text-xs text-slate-400 mt-1">Firma nel riquadro sottostante utilizzando la penna</p>
            </div>
            <div className="relative border-2 border-slate-300 rounded-xl bg-white overflow-hidden" style={{ touchAction: "none" }}>
              <canvas
                ref={canvasRef}
                className="w-full"
                style={{ height: "280px", cursor: "crosshair" }}
                onPointerDown={startDraw}
                onPointerMove={draw}
                onPointerUp={endDraw}
                onPointerLeave={endDraw}
                data-testid="signature-canvas"
              />
              {!hasDrawn && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="text-slate-300 text-lg font-medium">Firma qui</p>
                </div>
              )}
            </div>
            <div className="flex justify-center">
              <Button variant="outline" size="sm" onClick={clearCanvas} className="rounded-lg text-xs">
                <RotateCcw className="w-3 h-3 mr-1" /> Pulisci
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Footer buttons */}
      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
        {step < 3 && (
          <Button onClick={() => setStep(step + 1)} className="bg-red-600 hover:bg-red-700 text-white rounded-lg px-6" data-testid="signature-next-btn">
            Avanti <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
        {step === 3 && (
          <Button onClick={() => setStep(4)} className="bg-red-600 hover:bg-red-700 text-white rounded-lg px-6" data-testid="signature-sign-btn">
            <PenTool className="w-4 h-4 mr-2" /> Firma
          </Button>
        )}
        {step === 4 && (
          <Button onClick={confirmSignature} disabled={!hasDrawn} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-6" data-testid="signature-confirm-btn">
            <Check className="w-4 h-4 mr-2" /> Conferma Firma
          </Button>
        )}
      </div>
    </div>
  );
};

export default SignatureFlow;
